import { IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  GridViewLine,
  LineViewLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/assets";
import i18n from "@mirage/translations";
import { memo, useCallback, useState } from "react";
import styles from "./ConversationListPopup.module.css";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import type { ConversationMetadata } from "@mirage/service-conversation/types";
import type { ComposeSession } from "@mirage/shared/compose/compose-session";
import type { ComponentType } from "react";
import { ConversationRow, LegacySessionRow } from "./ConversationListRows";

interface ConversationListPopupProps {
  conversationsToDisplay: (ConversationMetadata | ComposeSession)[];
  onClickConversation: (conversation: ConversationMetadata) => void;
  onDeleteConversation?: (conversation: ConversationMetadata) => void;
  setShowModal: (show: boolean) => void;
  disabled: boolean;
  onOpenTemplates: () => void;
  handleLegacySessionClick: (session: ComposeSession) => void;
}

export const ConversationListPopup = memo(
  ({
    conversationsToDisplay,
    onClickConversation,
    onDeleteConversation,
    setShowModal,
    disabled,
    onOpenTemplates,
    handleLegacySessionClick,
  }: ConversationListPopupProps) => {
    const { reportPapEvent } = useMirageAnalyticsContext();

    const handleClickSeeMore = useCallback(() => {
      reportPapEvent(
        PAP_Click_DashChatAction({
          featureLine: "assist",
          dashChatAction: "see_more",
        }),
      );
      setShowModal(true);
    }, [setShowModal]);

    return (
      <Menu.Wrapper
        shouldPropagateClickOutsideMouseEvents
        closeOnSelection={true}
        onToggle={({ isOpen }) => {
          if (isOpen) {
            reportPapEvent(
              PAP_Click_DashChatAction({
                featureLine: "assist",
                dashChatAction: "select_dropdown_menu",
              }),
            );
          }
        }}
      >
        {({ getContentProps, getTriggerProps, closeMenu }) => (
          <>
            <IconButton
              {...getTriggerProps()}
              variant="borderless"
              aria-label={i18n.t("recents")}
              className={styles.ConversationListPopupTriggerButton}
              disabled={disabled}
            >
              <UIIcon src={LineViewLine} />
            </IconButton>
            <Menu.Content
              {...getContentProps()}
              className={styles.ConversationListPopupMenuContent}
            >
              <div className={styles.ConversationListPopupMenuHeader}>
                <Text
                  variant="label"
                  className={styles.ConversationListPopupMenuHeaderTitle}
                  isBold
                  size="small"
                  tagName="h2"
                  color="subtle"
                >
                  {i18n.t("recents")}
                </Text>
              </div>
              <Menu.Segment
                onClick={() => {
                  closeMenu({
                    immediate: true,
                  });
                }}
                className={styles.ConversationListPopupMenuSegment}
              >
                {conversationsToDisplay.map((item) => {
                  // Check if new session (has serverConversationId) or a legacy session
                  if ("serverConversationId" in item) {
                    return (
                      <ConversationRow
                        key={item.serverConversationId}
                        conversation={item}
                        isSelected={false}
                        onClickConversation={onClickConversation}
                        onDeleteConversation={onDeleteConversation}
                        largeIcon
                      />
                    );
                  } else {
                    return (
                      <LegacySessionRow
                        key={item.id}
                        session={item}
                        onClickSession={handleLegacySessionClick}
                      />
                    );
                  }
                })}
              </Menu.Segment>
              <Menu.ActionItem
                withTitle={i18n.t("answers_see_more")}
                onClick={handleClickSeeMore}
                className={styles.ConversationListPopupActionItem}
              />
              <div className={styles.ConversationListPopupMenuHeader}>
                <Text
                  variant="label"
                  className={styles.ConversationListPopupMenuHeaderTitle}
                  isBold
                  size="small"
                  tagName="h2"
                  color="subtle"
                >
                  {i18n.t("assist_templates_title")}
                </Text>
              </div>
              <Menu.Segment>
                <Menu.ActionItem
                  className={styles.ConversationListPopupActionItem}
                  withTitle={i18n.t("assist_templates_explore")}
                  withLeftAccessory={
                    <UIIcon
                      src={GridViewLine}
                      className={styles.ConversationListPopupGridIcon}
                    />
                  }
                  onClick={onOpenTemplates}
                />
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);
ConversationListPopup.displayName = "ConversationListPopup";

interface ConversationMenuItemProps {
  conversation: ConversationMetadata;
  onClickConversation: (conversation: ConversationMetadata) => void;
  onDeleteConversation?: (conversation: ConversationMetadata) => void;
  getIconForConversation: (conversation: ConversationMetadata) => ComponentType;
}
const ConversationMenuItem = memo(
  ({
    conversation,
    onClickConversation,
    onDeleteConversation,
    getIconForConversation,
  }: ConversationMenuItemProps) => {
    const [showActions, setShowActions] = useState(false);

    const handleClick = useCallback(() => {
      onClickConversation(conversation);
    }, [conversation, onClickConversation]);

    const handleDeleteClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDeleteConversation) {
          onDeleteConversation(conversation);
        }
      },
      [conversation, onDeleteConversation],
    );

    const handleMouseEnter = useCallback(() => {
      setShowActions(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setShowActions(false);
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Delete" && onDeleteConversation) {
          onDeleteConversation(conversation);
        }
      },
      [conversation, onDeleteConversation],
    );

    return (
      <div
        className={styles.ConversationListPopupMenuItem}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
      >
        <Menu.ActionItem
          key={conversation.serverConversationId}
          value={conversation.serverConversationId}
          withTitle={conversation.title}
          withLeftAccessory={
            <UIIcon src={getIconForConversation(conversation)} />
          }
          onClick={handleClick}
        />
        {showActions && onDeleteConversation && (
          <button
            className={styles.ConversationListPopupMenuItemActions}
            onClick={handleDeleteClick}
            aria-label={i18n.t("delete")}
          >
            <UIIcon src={MoreHorizontalLine} />
          </button>
        )}
      </div>
    );
  },
);
ConversationMenuItem.displayName = "ConversationMenuItem";
